import React, { useState, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Accordion, Card } from 'react-bootstrap'

import CampaignHeader, { tcLabelOptions } from 'components/Recommendations/Campaign/CampaignHeader'
import { CampaignEditor } from 'components/Recommendations/Campaign/Edit'
import TipDialogueBox from 'components/Shared/TipDialogueBox'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import InfoBox from 'components/Shared/InfoBox'
import { useCampaignSetupDispatch, useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import { validateMasterLanguage, validateLocalizations } from 'api/models'

const Ads = ({ event, campaigns, onNext, setShowCancelSetupModal, onBack }) => {
  const { t } = useTranslation()
  const { isStepValid, isClusterValid } = useCampaignSetupDispatch()
  const { setup } = useCampaignSetup()

  // Updated activeKey to be initially null so no accordion is open on load
  const [activeKey, setActiveKey] = useState(0)
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  // Use an array of refs for each accordion
  const accordionRefs = useRef([])
  
  // Check validation for each campaign
  const campaignValidationStatus = useMemo(() => {
    const status = {}
    
    campaigns.forEach((campaign) => {
      const creative = setup?.creatives?.find((x) => x.tc === campaign.tc)
      if (!creative) {
        status[campaign.tc] = false
        return
      }
      
      // Validate master language
      const masterValidation = validateMasterLanguage(creative)
      
      // Validate localizations
      let localizationsValid = true
      if (creative.localizations && typeof creative.localizations === 'object') {
        const masterFilledCount = {
          headline: (creative.headline || []).filter(Boolean).length,
          body: (creative.body || []).filter(Boolean).length
        }
        const localizationResult = validateLocalizations(creative, masterFilledCount)
        localizationsValid = localizationResult.valid
      }
      
      status[campaign.tc] = !masterValidation.valid || !localizationsValid
    })
    
    return status
  }, [campaigns, setup, showValidationErrors])

  // Function to handle opening/closing accordion and setting activeKey
  const handleAccordionClick = (index) => {
    setActiveKey(activeKey === index.toString() ? null : index.toString())
  }
  return (
    <div id="ads">
      <div id="campaigns-container">
        <TipDialogueBox text={t('Recommendations.addCreativeTextTDB')} />
        <br />
        <br />
        <Accordion defaultActiveKey="" activeKey={activeKey?.toString()}>
          {campaigns.map((campaign, index) => (
            <Card
              className="campaign"
              key={index}
              ref={(el) => (accordionRefs.current[index] = el)}
            >
              <Accordion.Toggle as={Card.Header} eventKey={index.toString()} className="pt-3">
                <CampaignHeader
                  campaign={campaign}
                  headerStatus={
                    isClusterValid(campaign.tc, campaign.tc_run_id)
                      ? tcLabelOptions.completed
                      : tcLabelOptions.pending
                  }
                  onClick={() => handleAccordionClick(index)}
                  hasValidationErrors={showValidationErrors && campaignValidationStatus[campaign.tc]}
                />
              </Accordion.Toggle>
              <Accordion.Collapse
                eventKey={index.toString()}
                onEntered={() => {
                  if (accordionRefs.current[index]) {
                    accordionRefs.current[index].scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    })
                  }
                }}
              >
                <Card.Body className="pt-0">
                  <hr className="divider" />
                  <CampaignEditor
                    eventId={event.id}
                    eventDate={event.date}
                    campaign={campaign}
                    eventInfo={event}
                    showValidationErrors={showValidationErrors}
                  />
                </Card.Body>
              </Accordion.Collapse>
            </Card>
          ))}
        </Accordion>
      </div>

      <div
        id="setup-btn-container"
        className="recommendation-setup-action-buttons d-flex justify-content-between align-items-center"
      >
        <div className="d-flex justify-content-between w-100">
          <TransparentBgButton
            className="bt"
            onClick={() => onBack()}
            text={t('Recommendations.btnBack')}
            fullWidth={false}
            color="navy"
          />
          <div className="d-flex gap-3">
            <TransparentBgButton
              text={t('Recommendations.btnCancelSetup')}
              color="navy"
              onClick={(e) => {
                e.preventDefault()
                setShowCancelSetupModal(true)
              }}
            />
            <InfoBox
              body={t('Please fill all required fields')}
              withAnchor={false}
              enabled={!isStepValid(3)}
            >
              <SecondaryButton
              // disabled={!isStepValid(3)}
                className="bt"
                onClick={() => {
                  setShowValidationErrors(true)
                  if (isStepValid(3)) {
                    onNext()
                  }
                }}
                text={t('Recommendations.btnNext')}
                fullWidth={false}
                classNames={!isStepValid(3) ? 'bt-disabled' : ''}
              />
            </InfoBox>
          </div>
        </div>
      </div>
    </div>
  )
}

Ads.propTypes = {
  event: PropTypes.object.isRequired,
  campaigns: PropTypes.array.isRequired,
  onNext: PropTypes.func.isRequired,
  setShowCancelSetupModal: PropTypes.func.isRequired,
  onBack: PropTypes.func,
}

export default Ads
